'use client';

import { LoadingSpinnerComponent } from '@/components/loading-spinner/loading-spinner';
import { RichTextRenderer } from '@/components/rich-text/rich-text-renderer';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useFooterLegalPolicies } from '@/hooks/content/legal-policies';
import { FooterLegalPolicies } from '@/types/legal-policies';
import React, { useState } from 'react';
import termsPrivacyModalStyles from './terms-privacy-modal.module.scss';

type TermsPrivacyModalProps = {
  children: React.ReactNode;
  fallbackTitle: string;
  policyPageType: keyof FooterLegalPolicies;
};

export const TermsPrivacyModal = ({ children, fallbackTitle, policyPageType }: TermsPrivacyModalProps): React.JSX.Element => {
  const [open, setOpen] = useState(false);
  const { data: policyPages, isPending } = useFooterLegalPolicies(open);
  const content = policyPages?.[policyPageType];
  const showLoading = open && isPending && !content;

  return (
    <Dialog open={open}
      onOpenChange={setOpen}
    >
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>

      <DialogContent className={termsPrivacyModalStyles['modal-content']}>
        <DialogHeader>
          <DialogDescription className="sr-only">
            {`Read the full ${fallbackTitle}.`}
          </DialogDescription>
          <DialogTitle data-test="terms-privacy-modal-title">
            {content?.headline ?? fallbackTitle}
          </DialogTitle>
        </DialogHeader>
        <div className={termsPrivacyModalStyles['modal-body']}
          data-test="terms-privacy-modal-body"
        >
          {showLoading &&
            <LoadingSpinnerComponent center="within"
              className="min-h-32"
            />
          }
          {!showLoading && content && <RichTextRenderer content={content.mainContent} />}
          {!showLoading && !content &&
            <p data-test="terms-privacy-modal-error">Error loading content</p>
          }
        </div>
      </DialogContent>
    </Dialog>
  );
};
